import AppImage from '@/components/AppImage'
import { ImageFieldProps } from '@/types/types'
import Link from 'next/link'

export type DetailsCardProps = {
  image?: ImageFieldProps
  title: string
  subtitle?: string
  text?: string
  email?: string
  link?: {
    url: string
    title?: string
    target?: string
  }
  roundedImages?: boolean
}

export default function DetailsCard({
  image,
  title,
  subtitle,
  text,
  email,
  link,
  roundedImages = true,
}: DetailsCardProps) {
  const detailsClass = `
    [&_svg]:open:rotate-180

    theme--base-0
    bg-theme 
    text-fg 
    border 
    rounded-md 

    flex
    flex-col
    
    @container/item 
  `

  const summaryClass = `
    flex 
    gap-m
    items-center
    justify-between
    p-s
    w-full
    
    @[480px]/item:p-m 
  `

  const titleClass = `
    flex 
    flex-col
    items-start
    gap-m

    @[320px]/item:min-h-[90px]
    @[480px]/item:min-h-[120px]

    @[320px]/item:flex-row
    @[320px]/item:items-center
  `

  const contentClass = `
    border-t 
    p-s
    w-full
    @[480px]/item:p-m 
  `

  const iconClass = `
    aspect-square 
    pointer-events-none
    overflow-hidden 
    
    w-[90px]
    @[480px]/item:w-[120px]
  `

  const imageEl = image?.url ? (
    <AppImage
      path={image.url}
      alt={title}
      className={`flex-shrink-0 ${iconClass} ${roundedImages ? 'rounded-full' : ''}`}
      width={104}
      height={104}
      transforms={{
        height: '120',
        width: '120',
      }}
      sizes="min(200px, calc(100vw - 40px))"
      srcSet={{
        120: { width: '120', height: '120' },
        240: { width: '240', height: '240' },
        360: { width: '360', height: '360' },
        480: { width: '480', height: '480' },
      }}
    />
  ) : null

  return (
    <>
      {text ? (
        <details className={detailsClass}>
          <summary className={`${summaryClass} cursor-pointer`}>
            <div className={titleClass}>
              {imageEl ? imageEl : null}

              <div className="flex w-full flex-col gap-xxxs">
                <span className="h5 @[480px]/item:h4 !mb-0">{title}</span>
                {subtitle ? (
                  <span className="p--small mb-0 leading-tight text-fg-body">{subtitle}</span>
                ) : null}

                {email || link ? (
                  <div className="flex items-center gap-xxs">
                    {email ? (
                      <Link href={`mailto:${email}`} className="break-all text-primary">
                        Email
                      </Link>
                    ) : null}
                    {email && link.url ? ' | ' : null}
                    {link.url ? (
                      <Link
                        href={link.url}
                        className="text-primary"
                        target={link?.target ?? '_self'}>
                        {link.title ? link.title : 'More details'}
                      </Link>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>

            <svg aria-hidden="true" className="aspect-square w-[32px] shrink-0 text-fg-title">
              <use href={`${process.env.NEXT_PUBLIC_SPRITE_PATH}#icon-chevron-down`}></use>
            </svg>
          </summary>

          <div className={contentClass}>
            <div className="p--small" dangerouslySetInnerHTML={{ __html: text }} />
          </div>
        </details>
      ) : (
        <div className={detailsClass}>
          <div className={summaryClass}>
            <div className={titleClass}>
              {imageEl ? imageEl : null}

              <div className="flex w-full flex-col">
                <span className="h5 @[480px]/item:h4 !mb-0">{title}</span>
                {subtitle ? (
                  <span className="p--small mb-0 leading-[130%] text-fg-body">{subtitle}</span>
                ) : null}

                {email || link ? (
                  <div className="flex items-center gap-xxs">
                    {email ? (
                      <Link href={`mailto:${email}`} className="break-all text-primary">
                        Email
                      </Link>
                    ) : null}
                    {email && link.url ? ' | ' : null}
                    {link.url ? (
                      <Link
                        href={link.url}
                        className="text-primary"
                        target={link?.target ?? '_self'}>
                        {link.title ? link.title : 'More details'}
                      </Link>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
